import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Image as ImageIcon,
  Info,
  MessageSquare,
  Plus,
  Reply,
  Settings,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { cx } from "../../utils";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input, { Label, Textarea } from "../../components/ui/Input";

/**
 * Editor for an app's "How to use" tab (Apps.usage).
 *
 * The customer's app page renders this content in a fixed layout
 * (frontend/src/layouts/App/UsagePanel.jsx): per section, where to find the
 * feature, a button that opens it, an optional screenshot, numbered steps, the
 * app's actions and tips. The preview beside the editor is that same layout.
 *
 * Sections carry a client-only `_key` for React; the server rebuilds every
 * section from known fields, so it is never stored.
 */

export const ICONS = [
  { id: "message", label: "Message", icon: MessageSquare },
  { id: "reply", label: "Reply", icon: Reply },
  { id: "workflow", label: "Workflow", icon: Zap },
  { id: "settings", label: "Settings", icon: Settings },
];
const iconFor = (id) => (ICONS.find((i) => i.id === id) || { icon: BookOpen }).icon;

// Pages in the customer's own account. The app page swaps {locationId} for the
// viewer's account and puts the CRM's own (white-label) domain in front.
const PAGES = [
  { label: "Open Conversations", path: "/v2/location/{locationId}/conversations/conversations", name: "Conversations" },
  { label: "Open Workflows", path: "/v2/location/{locationId}/automation/workflows", name: "Workflows" },
];

let keySeq = 0;
export const withKeys = (usage) => ({
  intro: usage?.intro || "",
  sections: (usage?.sections || []).map((s) => ({ ...s, _key: `s${++keySeq}` })),
});

const blankSection = () => ({
  _key: `s${++keySeq}`,
  icon: "message",
  title: "",
  summary: "",
  where: [],
  link: { label: "", path: "" },
  image: { src: "", alt: "", caption: "" },
  steps: [],
  actions: [],
  tips: [],
});

/** Problems the server would refuse, worded for the admin. Empty when fine. */
export const usageProblems = (usage) => {
  const out = [];
  (usage?.sections || []).forEach((s, i) => {
    const name = s.title?.trim() ? `"${s.title.trim()}"` : `Section ${i + 1}`;
    if (!s.title?.trim()) out.push(`${name} needs a title.`);
    const path = s.link?.path?.trim();
    if (path && (!path.startsWith("/") || path.startsWith("//"))) out.push(`${name}: the button link must start with a single "/".`);
    const src = s.image?.src?.trim();
    if (src && !/^https?:\/\//i.test(src)) out.push(`${name}: the screenshot must be a web address starting with https://.`);
  });
  return out;
};

const move = (list, index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/* ------------------------------------------------------------ list inputs */

const SmallIconButton = ({ label, onClick, disabled, children, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={cx(
      "grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-400 transition-colors disabled:opacity-30",
      danger ? "hover:bg-rose-50 hover:text-rose-600" : "hover:bg-ink-100 hover:text-ink-700",
    )}
  >
    {children}
  </button>
);

/** One line of text per item, reorderable — steps and tips. */
const TextList = ({ items, onChange, placeholder, numbered, addLabel }) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-2">
        <span
          className={cx(
            "mt-2 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold",
            numbered ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200/60" : "bg-ink-100 text-ink-500",
          )}
        >
          {numbered ? i + 1 : <Info size={11} />}
        </span>
        <Textarea
          rows={2}
          className="min-h-0 py-2 text-[13px] leading-5"
          value={item}
          onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
          placeholder={placeholder}
        />
        <div className="flex flex-col pt-1">
          <SmallIconButton label="Move up" onClick={() => onChange(move(items, i, -1))} disabled={i === 0}>
            <ArrowUp size={13} />
          </SmallIconButton>
          <SmallIconButton label="Move down" onClick={() => onChange(move(items, i, 1))} disabled={i === items.length - 1}>
            <ArrowDown size={13} />
          </SmallIconButton>
        </div>
        <div className="pt-1">
          <SmallIconButton label="Remove" danger onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <Trash2 size={13} />
          </SmallIconButton>
        </div>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange([...items, ""])}
      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-medium text-brand-700 hover:bg-brand-50"
    >
      <Plus size={13} /> {addLabel}
    </button>
  </div>
);

/** "Where to find it" — the trail of clicks, typed as chips. */
const CrumbInput = ({ items, onChange }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div className="rounded-lg border border-ink-200 bg-white p-1.5 shadow-sm focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-500/20">
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((crumb, i) => (
          <span key={i} className="inline-flex items-center gap-1.5">
            {i > 0 && <ChevronRight size={12} className="text-ink-300" aria-hidden />}
            <span className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-ink-50 py-0.5 pl-2 pr-1 text-[12px] font-medium text-ink-700">
              {crumb}
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="grid h-4 w-4 place-items-center rounded text-ink-400 hover:bg-ink-200 hover:text-ink-700"
                aria-label={`Remove ${crumb}`}
              >
                <X size={11} />
              </button>
            </span>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            } else if (e.key === "Backspace" && !draft && items.length) {
              onChange(items.slice(0, -1));
            }
          }}
          onBlur={add}
          placeholder={items.length ? "Next step…" : "e.g. Conversations — press Enter after each"}
          className="h-7 min-w-[10rem] flex-1 border-0 bg-transparent px-1.5 text-[13px] text-ink-900 placeholder:text-ink-400 focus:outline-none"
        />
      </div>
    </div>
  );
};

/** Actions: a name and what it does. */
const ActionList = ({ items, onChange }) => (
  <div className="space-y-2">
    {items.map((action, i) => (
      <div key={i} className="flex items-start gap-2 rounded-lg border border-ink-200 bg-white p-2">
        <Zap size={13} className="mt-2.5 shrink-0 text-violet-500" aria-hidden />
        {/* Descriptions run to a few sentences, so they get a box that wraps. */}
        <div className="grid min-w-0 flex-1 gap-1.5">
          <Input
            className="h-9 text-[13px] font-medium"
            maxLength={80}
            value={action.name || ""}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            placeholder="Action name"
          />
          <Textarea
            rows={2}
            maxLength={300}
            className="min-h-0 py-2 text-[13px] leading-5"
            value={action.description || ""}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
            placeholder="What it does"
          />
        </div>
        <SmallIconButton label="Move up" onClick={() => onChange(move(items, i, -1))} disabled={i === 0}>
          <ArrowUp size={13} />
        </SmallIconButton>
        <SmallIconButton label="Remove" danger onClick={() => onChange(items.filter((_, j) => j !== i))}>
          <Trash2 size={13} />
        </SmallIconButton>
      </div>
    ))}
    <button
      type="button"
      onClick={() => onChange([...items, { name: "", description: "" }])}
      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-medium text-brand-700 hover:bg-brand-50"
    >
      <Plus size={13} /> Add action
    </button>
  </div>
);

const Group = ({ title, hint, children }) => (
  <div className="space-y-2">
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">{title}</p>
      {hint && <p className="text-[11.5px] text-ink-400">{hint}</p>}
    </div>
    {children}
  </div>
);

/* ---------------------------------------------------------------- section */

const SectionEditor = ({ section, index, count, open, onToggle, onChange, onMove, onRemove }) => {
  const Icon = iconFor(section.icon);
  const set = (key, value) => onChange({ ...section, [key]: value });
  const link = section.link || { label: "", path: "" };
  const image = section.image || { src: "", alt: "", caption: "" };
  const summaryBits = [
    section.steps?.length && `${section.steps.length} step${section.steps.length === 1 ? "" : "s"}`,
    section.actions?.length && `${section.actions.length} action${section.actions.length === 1 ? "" : "s"}`,
    section.tips?.length && `${section.tips.length} tip${section.tips.length === 1 ? "" : "s"}`,
    link.path && "button",
    image.src && "screenshot",
  ].filter(Boolean);

  return (
    <div className={cx("overflow-hidden rounded-2xl border bg-white shadow-card", open ? "border-brand-200" : "border-ink-200/70")}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open}>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
            <Icon size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className={cx("block truncate text-[14px] font-semibold", section.title ? "text-ink-900" : "text-rose-600")}>
              {section.title || "Untitled section"}
            </span>
            <span className="block truncate text-[12px] text-ink-500">{summaryBits.length ? summaryBits.join(" · ") : "Empty"}</span>
          </span>
          <ChevronDown size={16} className={cx("shrink-0 text-ink-400 transition-transform", open && "rotate-180")} />
        </button>
        <div className="flex items-center">
          <SmallIconButton label="Move section up" onClick={() => onMove(-1)} disabled={index === 0}>
            <ArrowUp size={14} />
          </SmallIconButton>
          <SmallIconButton label="Move section down" onClick={() => onMove(1)} disabled={index === count - 1}>
            <ArrowDown size={14} />
          </SmallIconButton>
          <SmallIconButton label="Delete section" danger onClick={onRemove}>
            <Trash2 size={14} />
          </SmallIconButton>
        </div>
      </div>

      {open && (
        <div className="space-y-6 border-t border-ink-150 bg-ink-50/40 px-4 py-5 sm:px-5">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
            <div>
              <Label required>Title</Label>
              <Input className="mt-1.5" maxLength={120} value={section.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Send SMS from Conversations" />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="mt-1.5 flex gap-1 rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
                {ICONS.map(({ id, label, icon: I }) => (
                  <button
                    key={id}
                    type="button"
                    title={label}
                    aria-label={label}
                    aria-pressed={section.icon === id}
                    onClick={() => set("icon", id)}
                    className={cx("grid h-8 w-8 place-items-center rounded-md transition-colors", section.icon === id ? "bg-ink-900 text-white" : "text-ink-500 hover:bg-ink-100")}
                  >
                    <I size={15} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label hint="one line under the title">Summary</Label>
            <Input className="mt-1.5" maxLength={300} value={section.summary || ""} onChange={(e) => set("summary", e.target.value)} placeholder="Message a contact from their conversation, on your number." />
          </div>

          <Group title="Where to find it" hint="The clicks to reach the feature">
            <CrumbInput items={section.where || []} onChange={(v) => set("where", v)} />
          </Group>

          <Group title="Open button" hint="Opens the page in the customer's own account">
            <div className="grid gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
              <Input className="h-9 text-[13px]" maxLength={40} value={link.label} onChange={(e) => set("link", { ...link, label: e.target.value })} placeholder="Open Conversations" />
              <Input className="h-9 font-mono text-[12px]" maxLength={300} value={link.path} onChange={(e) => set("link", { ...link, path: e.target.value })} placeholder="/v2/location/{locationId}/…" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11.5px] text-ink-400">Quick pick:</span>
              {PAGES.map((p) => (
                <button
                  key={p.path}
                  type="button"
                  onClick={() => set("link", { label: link.label?.trim() ? link.label : p.label, path: p.path })}
                  className={cx(
                    "rounded-full px-2.5 py-0.5 text-[11.5px] font-medium ring-1 transition-colors",
                    link.path === p.path ? "bg-brand-600 text-white ring-brand-600" : "bg-white text-ink-600 ring-ink-200 hover:bg-ink-50",
                  )}
                >
                  {p.name}
                </button>
              ))}
              {link.path && (
                <button type="button" onClick={() => set("link", { label: "", path: "" })} className="text-[11.5px] font-medium text-ink-400 hover:text-ink-700">
                  No button
                </button>
              )}
            </div>
            <p className="text-[11.5px] leading-relaxed text-ink-400">
              <code className="font-mono">{"{locationId}"}</code> becomes the viewer's account, and the CRM's own domain goes in front — so it works on any white-label domain.
            </p>
          </Group>

          <Group title="Screenshot" hint="Optional">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              <Input className="h-9 text-[13px]" value={image.src} onChange={(e) => set("image", { ...image, src: e.target.value })} placeholder="https://… image address" />
              {image.src && /^https?:\/\//i.test(image.src) ? (
                <img src={image.src} alt="" className="h-9 w-16 rounded-md border border-ink-200 object-cover" />
              ) : (
                <span className="grid h-9 w-16 place-items-center rounded-md border border-dashed border-ink-200 text-ink-300">
                  <ImageIcon size={14} />
                </span>
              )}
            </div>
            {image.src && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Input className="h-9 text-[13px]" value={image.alt} onChange={(e) => set("image", { ...image, alt: e.target.value })} placeholder="Describe the image (for screen readers)" />
                <Input className="h-9 text-[13px]" value={image.caption} onChange={(e) => set("image", { ...image, caption: e.target.value })} placeholder="Caption (optional)" />
              </div>
            )}
          </Group>

          <Group title="How to use it" hint="Numbered on the page">
            <TextList numbered items={section.steps || []} onChange={(v) => set("steps", v)} placeholder="What to do in this step" addLabel="Add step" />
          </Group>

          <Group title="Available actions" hint="Workflow actions this app adds">
            <ActionList items={section.actions || []} onChange={(v) => set("actions", v)} />
          </Group>

          <Group title="Tips">
            <TextList items={section.tips || []} onChange={(v) => set("tips", v)} placeholder="Something worth knowing" addLabel="Add tip" />
          </Group>
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- preview */

// Mirrors the Section card in frontend/src/layouts/App/UsagePanel.jsx.
const SectionPreview = ({ section }) => {
  const Icon = iconFor(section.icon);
  const steps = (section.steps || []).filter((x) => x.trim());
  const actions = (section.actions || []).filter((a) => a.name?.trim());
  const tips = (section.tips || []).filter((x) => x.trim());
  const where = section.where || [];
  const img = /^https?:\/\//i.test(section.image?.src || "") ? section.image : null;

  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
      <div className="flex items-start gap-3 px-4 pt-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
          <Icon size={16} />
        </span>
        <div className="min-w-0">
          <h3 className="text-[14.5px] font-semibold tracking-tight text-ink-900">{section.title || "Untitled section"}</h3>
          {section.summary && <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{section.summary}</p>}
        </div>
      </div>
      <div className="space-y-4 px-4 pb-4 pt-3">
        {where.length > 0 && (
          <div>
            <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Where to find it</p>
            <div className="flex flex-wrap items-center gap-1">
              {where.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  {i > 0 && <ChevronRight size={11} className="text-ink-300" />}
                  <span className="rounded-md border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[11.5px] font-medium text-ink-700">{c}</span>
                </span>
              ))}
            </div>
            {section.link?.path && (
              <span className="mt-2.5 inline-flex h-7 items-center gap-1.5 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 px-2.5 text-[12px] font-semibold text-white shadow-brand-glow">
                {section.link.label || "Open"} <ExternalLink size={12} />
              </span>
            )}
          </div>
        )}
        {img && (
          <figure className="overflow-hidden rounded-lg border border-ink-200/70 bg-ink-50">
            <img src={img.src} alt={img.alt || ""} className="block w-full" />
            {img.caption && <figcaption className="border-t border-ink-200/70 bg-white px-3 py-1.5 text-[11.5px] text-ink-500">{img.caption}</figcaption>}
          </figure>
        )}
        {steps.length > 0 && (
          <div>
            <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">How to use it</p>
            <ol className="space-y-2">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-50 text-[10.5px] font-semibold text-brand-700 ring-1 ring-brand-200/60">{i + 1}</span>
                  <span className="text-[12.5px] leading-relaxed text-ink-700">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
        {actions.length > 0 && (
          <div>
            <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">Available actions</p>
            <ul className="divide-y divide-ink-150 overflow-hidden rounded-lg border border-ink-200/70">
              {actions.map((a, i) => (
                <li key={i} className="flex gap-2.5 px-3 py-2">
                  <Zap size={12} className="mt-1 shrink-0 text-violet-500" />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-ink-900">{a.name}</p>
                    {a.description && <p className="text-[12px] leading-relaxed text-ink-500">{a.description}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {tips.length > 0 && (
          <ul className="space-y-1.5 rounded-lg bg-ink-50 px-3 py-2.5">
            {tips.map((t, i) => (
              <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-ink-600">
                <Info size={12} className="mt-0.5 shrink-0 text-ink-400" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------- main */

const UsageEditor = ({ usage, onChange }) => {
  const sections = usage.sections || [];
  const [openKey, setOpenKey] = useState(sections[0]?._key || null);
  const [previewKey, setPreviewKey] = useState(null);

  const setSections = (next) => onChange({ ...usage, sections: next });
  const previewed = sections.find((s) => s._key === (previewKey || openKey)) || sections[0];

  const addSection = () => {
    const s = blankSection();
    setSections([...sections, s]);
    setOpenKey(s._key);
    setPreviewKey(null);
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="min-w-0 space-y-4">
        <Card title="Introduction" subtitle="One or two sentences above the sections on the app's How to use tab.">
          <Textarea
            rows={2}
            maxLength={500}
            value={usage.intro || ""}
            onChange={(e) => onChange({ ...usage, intro: e.target.value })}
            placeholder="Once setup is done, this is where everything the app adds lives in your CRM, and how to use it."
          />
        </Card>

        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-semibold tracking-tight text-ink-900">Sections</h3>
            <p className="text-xs text-ink-500">One per feature, in the order the page shows them.</p>
          </div>
          <Button type="button" size="sm" variant="secondary" onClick={addSection} icon={<Plus size={14} />}>
            Add section
          </Button>
        </div>

        {sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink-200 bg-white px-6 py-12 text-center">
            <p className="text-sm font-medium text-ink-700">No sections yet</p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-ink-500">
              Without sections, the app page doesn't show a How to use tab. Add one for each feature — sending a message, replies, workflow actions.
            </p>
            <Button type="button" size="sm" variant="secondary" onClick={addSection} className="mt-4" icon={<Plus size={14} />}>
              Add the first section
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sections.map((s, i) => (
              <div key={s._key} onFocusCapture={() => setPreviewKey(s._key)} onMouseEnter={() => setPreviewKey(s._key)}>
                <SectionEditor
                  section={s}
                  index={i}
                  count={sections.length}
                  open={openKey === s._key}
                  onToggle={() => setOpenKey((k) => (k === s._key ? null : s._key))}
                  onChange={(next) => setSections(sections.map((x) => (x._key === s._key ? next : x)))}
                  onMove={(delta) => setSections(move(sections, i, delta))}
                  onRemove={() => setSections(sections.filter((x) => x._key !== s._key))}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <aside className="xl:sticky xl:top-4 xl:self-start">
        <div className="rounded-2xl border border-ink-200/70 bg-ink-50/60 p-3">
          <div className="mb-3 flex items-center justify-between gap-2 px-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Preview</p>
            <p className="text-[11px] text-ink-400">As customers see it</p>
          </div>
          {sections.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1">
              {sections.map((s, i) => (
                <button
                  key={s._key}
                  type="button"
                  onClick={() => setPreviewKey(s._key)}
                  className={cx(
                    "max-w-[11rem] truncate rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 transition-colors",
                    previewed?._key === s._key ? "bg-white text-ink-900 shadow-sm ring-ink-200" : "text-ink-500 ring-transparent hover:bg-white/70",
                  )}
                >
                  {s.title || `Section ${i + 1}`}
                </button>
              ))}
            </div>
          )}
          {usage.intro && <p className="mb-3 px-1 text-[12.5px] leading-relaxed text-ink-500">{usage.intro}</p>}
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            {previewed ? (
              <SectionPreview section={previewed} />
            ) : (
              <p className="rounded-xl border border-dashed border-ink-200 bg-white px-4 py-10 text-center text-[12.5px] text-ink-400">
                Add a section to see it here.
              </p>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default UsageEditor;
